import { VibeshipConfig, PartialVibeshipConfig, DeepPartial } from './types';

/**
 * Deep merge utility for configuration objects
 */
export function deepMerge<T extends object>(
  target: T,
  ...sources: DeepPartial<T>[]
): T {
  if (!sources.length) return target;

  const source = sources.shift();
  if (!source) return target;

  const output = { ...target };

  Object.keys(source).forEach((key) => {
    const targetValue = output[key as keyof T];
    const sourceValue = source[key as keyof T];

    if (sourceValue === undefined) {
      return;
    }

    if (sourceValue === null) {
      output[key as keyof T] = null as any;
      return;
    }

    if (isObject(targetValue) && isObject(sourceValue)) {
      output[key as keyof T] = deepMerge(
        targetValue as any,
        sourceValue as any
      );
    } else if (Array.isArray(sourceValue)) {
      // For arrays, replace entirely rather than merge
      output[key as keyof T] = [...sourceValue] as any;
    } else {
      output[key as keyof T] = sourceValue as any;
    }
  });

  return deepMerge(output, ...sources);
}

/**
 * Check if value is a plain object
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    !(value instanceof RegExp)
  );
}

/**
 * Merge configurations with proper precedence
 * Priority (highest to lowest):
 * 1. User configuration
 * 2. Environment overrides
 * 3. Environment defaults
 * 4. Base defaults
 * 
 * @param defaultConfig - Base default configuration
 * @param environmentDefaults - Environment-specific defaults
 * @param environmentOverrides - Environment variable overrides
 * @param userConfig - User-provided configuration
 * @returns Merged configuration
 * 
 * @example
 * ```ts
 * const config = mergeConfigurations(
 *   defaultConfig,
 *   developmentConfig,
 *   envOverrides,
 *   userConfig
 * );
 * ```
 * 
 * @public
 */
export function mergeConfigurations(
  defaultConfig: VibeshipConfig,
  environmentDefaults: PartialVibeshipConfig,
  environmentOverrides: PartialVibeshipConfig,
  userConfig: PartialVibeshipConfig
): VibeshipConfig {
  return deepMerge(
    defaultConfig,
    environmentDefaults,
    environmentOverrides,
    userConfig
  );
}

/**
 * Apply environment variable overrides to configuration
 */
export function applyEnvironmentOverrides(
  config: VibeshipConfig
): VibeshipConfig {
  const overrides: PartialVibeshipConfig = {};

  // Authentication overrides
  if (process.env.VIBECODE_AUTH_SECRET) {
    overrides.security = {
      ...overrides.security,
      auth: {
        ...overrides.security?.auth,
        jwtSecret: process.env.VIBECODE_AUTH_SECRET,
      },
    };
  }

  if (process.env.VIBECODE_API_KEY) {
    overrides.security = {
      ...overrides.security,
      auth: {
        ...overrides.security?.auth,
        apiKey: process.env.VIBECODE_API_KEY,
      },
    };
  }

  // CORS overrides
  if (process.env.VIBECODE_ALLOWED_ORIGINS) {
    const origins = process.env.VIBECODE_ALLOWED_ORIGINS.split(',').map(s => s.trim());
    overrides.security = {
      ...overrides.security,
      corsConfig: {
        ...overrides.security?.corsConfig,
        allowedOrigins: origins,
      },
    };
  }

  // Rate limit overrides
  if (process.env.VIBECODE_RATE_LIMIT) {
    const [windowMs, maxRequests] = process.env.VIBECODE_RATE_LIMIT.split(',').map(Number);
    if (windowMs && maxRequests) {
      overrides.security = {
        ...overrides.security,
        rateLimitConfig: {
          ...overrides.security?.rateLimitConfig,
          windowMs,
          maxRequests,
        },
      };
    }
  }

  // Cache overrides
  if (process.env.VIBECODE_CACHE_ENABLED !== undefined) {
    overrides.cache = {
      ...overrides.cache,
      enabled: process.env.VIBECODE_CACHE_ENABLED === 'true',
    };
  }

  // AI provider overrides
  if (process.env.VIBECODE_AI_PROVIDER) {
    overrides.features = {
      ...overrides.features,
      ai: true,
    };
  }

  // Apply feature flags from environment
  const featureFlags = [
    'VIBECODE_FEATURE_TASKS',
    'VIBECODE_FEATURE_AI',
    'VIBECODE_FEATURE_REALTIME',
    'VIBECODE_FEATURE_COMMAND_PALETTE',
    'VIBECODE_FEATURE_FILE_EDITING',
    'VIBECODE_FEATURE_MARKDOWN_PREVIEW',
  ];

  featureFlags.forEach((flag) => {
    if (process.env[flag] !== undefined) {
      const featureName = flag
        .replace('VIBECODE_FEATURE_', '')
        .toLowerCase()
        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase()) as keyof typeof config.features;

      overrides.features = {
        ...overrides.features,
        [featureName]: process.env[flag] === 'true',
      };
    }
  });

  return deepMerge(config, overrides);
}

/**
 * Freeze configuration to prevent mutations
 */
export function freezeConfig<T extends object>(config: T): Readonly<T> {
  return deepFreeze(config);
}

/**
 * Deep freeze utility
 */
function deepFreeze<T extends object>(obj: T): Readonly<T> {
  Object.freeze(obj);

  Object.getOwnPropertyNames(obj).forEach((prop) => {
    const value = obj[prop as keyof T];
    if (value && typeof value === 'object' && !Object.isFrozen(value)) {
      deepFreeze(value);
    }
  });

  return obj;
}

/**
 * Create a configuration patch that can be applied later
 */
export function createConfigPatch(
  baseConfig: VibeshipConfig,
  newConfig: VibeshipConfig
): PartialVibeshipConfig {
  const patch: PartialVibeshipConfig = {};

  Object.keys(newConfig).forEach((key) => {
    const baseValue = baseConfig[key as keyof VibeshipConfig];
    const newValue = newConfig[key as keyof VibeshipConfig];

    if (JSON.stringify(baseValue) !== JSON.stringify(newValue)) {
      patch[key as keyof PartialVibeshipConfig] = newValue as any;
    }
  });

  return patch;
}

/**
 * Validate that a configuration patch is safe to apply
 */
export function isPatchSafe(patch: PartialVibeshipConfig): boolean {
  // Check for dangerous patterns in file paths
  const dangerousPaths = ['/', '..', '~', '$HOME'];
  
  // Check scanPaths
  if (patch.scanPaths && Array.isArray(patch.scanPaths)) {
    for (const path of patch.scanPaths) {
      if (path && dangerousPaths.some(dangerous => path.includes(dangerous))) {
        return false;
      }
    }
  }

  // Check security.allowedPaths
  if (patch.security?.allowedPaths && Array.isArray(patch.security.allowedPaths)) {
    for (const path of patch.security.allowedPaths) {
      if (path && dangerousPaths.some(dangerous => path.includes(dangerous))) {
        return false;
      }
    }
  }

  // Check for unsafe patterns in blocked patterns
  if (patch.security?.blockedPatterns) {
    // Ensure critical patterns are not removed
    const criticalPatterns = ['**/.env*', '**/*.key', '**/*.pem'];
    for (const pattern of criticalPatterns) {
      if (!patch.security.blockedPatterns.includes(pattern)) {
        return false;
      }
    }
  }

  return true;
}